import { TooltipOverflowMode } from "@fluentui/react";
import {
  formatRequestedLine,
  formatWindscreen,
  parseRequestedLines,
  REQUESTED_LINE_FIELDS,
} from "pages/vehicle/FinanceTab/FinanceForm/requestedLinesHelpers";
import React from "react";
import { useTranslation } from "react-i18next";
import FluentTooltip from "./FluentTooltip";

interface ClientCommentJsonDisplayProps {
  comment: string | null | undefined;
}

export const ClientCommentJsonDisplay: React.FC<
  ClientCommentJsonDisplayProps
> = ({ comment }) => {
  const { t } = useTranslation();

  if (!comment) {
    return null;
  }

  try {
    const commentData = JSON.parse(comment);

    // Austrian client offer: clientCoveragesInfo holds the desired insurance lines.
    if (commentData?.requestedLines) {
      const rl = parseRequestedLines(comment);
      const selected = REQUESTED_LINE_FIELDS.filter((f) => rl[f.key]).map(
        (f) => formatRequestedLine(rl, f.key, t)
      );
      // CZ: windscreen coverage, after the lines
      const windscreen = formatWindscreen(rl, t);
      if (windscreen) selected.push(windscreen);

      // Additional hull coverages the client requested (also stored in this JSON).
      const coverageMap: Record<string, string> = {
        gap: "bfm.vehicleForm.gap.label",
        replacementValue: "bfm.vehicleForm.replacementValue.label",
        grossNegligence: "bfm.vehicleForm.grossNegligence.label",
        electroPlusPackage: "bfm.vehicleForm.electroPlusPackage.label",
      };
      const cov = commentData.coverages || {};
      const selectedCoverages = Object.keys(coverageMap)
        .filter((k) => cov[k])
        .map((k) => t(coverageMap[k]));

      const parts = [...selected, ...selectedCoverages];
      const text = parts.join(", ");
      return (
        <FluentTooltip
          overflowMode={TooltipOverflowMode.Parent}
          content={text}
        >
          {text}
        </FluentTooltip>
      );
    }

    return (
      <>
        {Object.keys(commentData)
          .filter((domain) => commentData[domain]?.Enabled)
          .map((domain) => {
            const from = commentData[domain]?.CoverageFrom
              ? new Date(commentData[domain].CoverageFrom).toLocaleDateString(
                  "de-DE"
                )
              : "";
            const to = commentData[domain]?.CoverageTo
              ? new Date(commentData[domain].CoverageTo).toLocaleDateString(
                  "de-DE"
                )
              : "";

            const localizedDomain =
              t(domain) +
              (commentData[domain]?.Urgent
                ? ` - ${t("bfm.urgent.label")}`
                : "");

            return from || to
              ? `${localizedDomain} (${from}${from && to ? " - " : ""}${to})`
              : localizedDomain;
          })
          .join(" ")}
      </>
    );
  } catch (error) {
    // If parsing fails, return the original comment
    return (
      <FluentTooltip
        overflowMode={TooltipOverflowMode.Parent}
        content={comment}
      >
        {comment}
      </FluentTooltip>
    );
  }
};

export default ClientCommentJsonDisplay;
